/**
 * Knowledge Base Type Definitions
 *
 * Types for the structured Knowledge Base system with multiple article templates
 * (How-To, Known Error, SOP/Runbook) and department-based access control.
 */

// ============================================
// Article Category - software/tool based organization
// ============================================

export const ArticleCategory = {
  JABBER: 'Jabber',
  TEAMS: 'Teams',
  SERVICENOW: 'ServiceNow',
  VPN: 'VPN',
  O365: 'Office 365',
  OUTLOOK: 'Outlook',
  ONEDRIVE: 'OneDrive',
  SHAREPOINT: 'SharePoint',
  ACTIVE_DIRECTORY: 'Active Directory',
  MFA: 'MFA/Duo',
  PASSWORD: 'Password Management',
  PRINTER: 'Printers',
  HARDWARE: 'Hardware',
  NETWORK: 'Network',
  OTHER: 'Other',
} as const;
export type ArticleCategory = (typeof ArticleCategory)[keyof typeof ArticleCategory];

export const ARTICLE_CATEGORIES = Object.values(ArticleCategory);

// ============================================
// Article Type - template selection
// ============================================

export const ArticleType = {
  HOW_TO: 'how-to',
  KNOWN_ERROR: 'known-error',
  SOP_RUNBOOK: 'sop-runbook',
} as const;
export type ArticleType = (typeof ArticleType)[keyof typeof ArticleType];

export const ARTICLE_TYPES = Object.values(ArticleType);

/** Human-readable labels for article types */
export const ARTICLE_TYPE_LABELS: Record<ArticleType, string> = {
  'how-to': 'How-To Guide',
  'known-error': 'Known Error',
  'sop-runbook': 'SOP / Runbook',
};

/** Descriptions for template selection */
export const ARTICLE_TYPE_DESCRIPTIONS: Record<ArticleType, string> = {
  'how-to': 'Step-by-step instructions for completing a task or procedure',
  'known-error': 'Document a known issue with affected systems, symptoms, and workaround',
  'sop-runbook': 'Standard operating procedure with prerequisites, verification, and rollback',
};

// ============================================
// Department / Audience - access control
// ============================================

export const Department = {
  IT: 'IT',
  HR: 'HR',
  FINANCE: 'Finance',
  OPERATIONS: 'Operations',
  ALL: 'All Departments',
} as const;
export type Department = (typeof Department)[keyof typeof Department];

export const DEPARTMENTS = Object.values(Department);

/** Departments excluding "All" for specific selection */
export const SPECIFIC_DEPARTMENTS = DEPARTMENTS.filter((d) => d !== Department.ALL);

// ============================================
// Article Step - shared content unit for How-To and SOP/Runbook
// ============================================

export interface ArticleStep {
  id: string;
  stepNumber: number;
  title: string;
  content: string;
  imageUrl?: string;
  imageAlt?: string;
}

// ============================================
// Template-Specific Content Types
// ============================================

/**
 * How-To content - sequential steps for procedures
 */
export interface HowToContent {
  steps: ArticleStep[];
}

/**
 * Emergency contact for SOP/Runbook
 */
export interface EmergencyContact {
  id: string;
  name: string;
  role: string;
  contact: string;
}

/**
 * Known Error content - structured diagnostic information
 */
export interface KnownErrorContent {
  /** Systems or applications affected by this error */
  affectedSystems: string[];
  /** Observable issues that indicate this error */
  symptoms: string;
  /** Current fix or workaround for the issue */
  workaround: string;
  /** Links to related KB articles */
  relatedArticleIds: string[];
}

/**
 * SOP/Runbook content - comprehensive procedural documentation
 */
export interface SOPRunbookContent {
  /** Goal or purpose of the procedure */
  objective: string;
  /** Required conditions before starting */
  prerequisites: string[];
  /** Main procedure steps */
  procedureSteps: ArticleStep[];
  /** How to verify successful completion */
  verification: string;
  /** Steps to undo if needed */
  rollbackProcedure: string;
  /** Who to contact for issues */
  emergencyContacts: EmergencyContact[];
}

// ============================================
// Discriminated Union for Article Content
// ============================================

export type ArticleContent =
  | { type: 'how-to'; content: HowToContent }
  | { type: 'known-error'; content: KnownErrorContent }
  | { type: 'sop-runbook'; content: SOPRunbookContent };

// ============================================
// Article - main Knowledge Base entity
// ============================================

export interface Article {
  id: string;
  title: string;
  description: string;
  category: ArticleCategory;
  tags: string[];

  /** Article template type */
  articleType: ArticleType;

  /** Type-specific content (discriminated union) */
  articleContent: ArticleContent;

  /** Departments that can view this article */
  audiences: Department[];

  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  viewCount: number;
  isPublished: boolean;

  /**
   * @deprecated Use articleContent.content.steps for how-to articles.
   * Kept for backward compatibility during migration.
   */
  steps: ArticleStep[];
}

// ============================================
// Filters and Sorting
// ============================================

export interface ArticleFilters {
  searchQuery: string;
  categories: ArticleCategory[];
  articleTypes: ArticleType[];
  audiences: Department[];
}

export type ArticleSortField = 'title' | 'updatedAt' | 'viewCount' | 'category';
export type SortDirection = 'asc' | 'desc';

// ============================================
// Form Data Types
// ============================================

/** Form data for individual steps */
export interface StepFormData {
  id?: string;
  title: string;
  content: string;
  imageUrl?: string;
  imageAlt?: string;
}

/** Form data for emergency contacts */
export interface EmergencyContactFormData {
  id?: string;
  name: string;
  role: string;
  contact: string;
}

/** How-To form data */
export interface HowToFormData {
  steps: StepFormData[];
}

/** Known Error form data */
export interface KnownErrorFormData {
  affectedSystems: string[];
  symptoms: string;
  workaround: string;
  relatedArticleIds: string[];
}

/** SOP/Runbook form data */
export interface SOPRunbookFormData {
  objective: string;
  prerequisites: string[];
  procedureSteps: StepFormData[];
  verification: string;
  rollbackProcedure: string;
  emergencyContacts: EmergencyContactFormData[];
}

/** Discriminated union for form content */
export type ArticleContentFormData =
  | { type: 'how-to'; content: HowToFormData }
  | { type: 'known-error'; content: KnownErrorFormData }
  | { type: 'sop-runbook'; content: SOPRunbookFormData };

/** Complete article form data */
export interface ArticleFormData {
  title: string;
  description: string;
  category: ArticleCategory;
  tags: string[];
  articleType: ArticleType;
  articleContent: ArticleContentFormData;
  audiences: Department[];
  isPublished: boolean;
}

// ============================================
// Form Validation Errors
// ============================================

export interface ArticleFormErrors {
  // Common fields
  title?: string;
  description?: string;
  category?: string;
  audiences?: string;

  // How-To specific
  steps?: string;

  // Known Error specific
  affectedSystems?: string;
  symptoms?: string;
  workaround?: string;
  relatedArticles?: string;

  // SOP/Runbook specific
  objective?: string;
  prerequisites?: string;
  procedureSteps?: string;
  verification?: string;
  rollbackProcedure?: string;
  emergencyContacts?: string;

  // General errors
  general?: string;
}

// ============================================
// Context Value Type
// ============================================

export interface KnowledgeBaseContextValue {
  articles: Article[];
  filters: ArticleFilters;
  setFilters: (filters: ArticleFilters) => void;
  getArticleById: (id: string) => Article | undefined;
  getRelatedArticles: (articleIds: string[]) => Article[];
  createArticle: (data: ArticleFormData) => Promise<Article>;
  updateArticle: (id: string, data: ArticleFormData) => Promise<Article | undefined>;
  deleteArticle: (id: string) => Promise<void>;
  incrementViewCount: (id: string) => void;
  isLoading: boolean;
  userDepartment: Department;
  isAdmin: boolean;
}

// ============================================
// Image Constraints
// ============================================

export const IMAGE_CONSTRAINTS = {
  MAX_WIDTH: 800,
  MAX_HEIGHT: 600,
  QUALITY: 0.8,
  MAX_SIZE_BYTES: 500000,
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
} as const;

// ============================================
// Helper Functions
// ============================================

/** Create default How-To content */
export function createDefaultHowToContent(): HowToContent {
  return { steps: [] };
}

/** Create default Known Error content */
export function createDefaultKnownErrorContent(): KnownErrorContent {
  return {
    affectedSystems: [],
    symptoms: '',
    workaround: '',
    relatedArticleIds: [],
  };
}

/** Create default SOP/Runbook content */
export function createDefaultSOPRunbookContent(): SOPRunbookContent {
  return {
    objective: '',
    prerequisites: [],
    procedureSteps: [],
    verification: '',
    rollbackProcedure: '',
    emergencyContacts: [],
  };
}

/** Create default content for a given article type */
export function createDefaultArticleContent(type: ArticleType): ArticleContent {
  switch (type) {
    case ArticleType.HOW_TO:
      return { type: 'how-to', content: createDefaultHowToContent() };
    case ArticleType.KNOWN_ERROR:
      return { type: 'known-error', content: createDefaultKnownErrorContent() };
    case ArticleType.SOP_RUNBOOK:
      return { type: 'sop-runbook', content: createDefaultSOPRunbookContent() };
  }
}

/** Create default form data for a given article type */
export function createDefaultFormContent(type: ArticleType): ArticleContentFormData {
  switch (type) {
    case ArticleType.HOW_TO:
      return { type: 'how-to', content: { steps: [] } };
    case ArticleType.KNOWN_ERROR:
      return {
        type: 'known-error',
        content: {
          affectedSystems: [],
          symptoms: '',
          workaround: '',
          relatedArticleIds: [],
        },
      };
    case ArticleType.SOP_RUNBOOK:
      return {
        type: 'sop-runbook',
        content: {
          objective: '',
          prerequisites: [],
          procedureSteps: [],
          verification: '',
          rollbackProcedure: '',
          emergencyContacts: [],
        },
      };
  }
}

/** Check if user has access to an article based on department */
export function hasAccessToArticle(
  article: Article,
  userDepartment: Department,
  isAdmin: boolean
): boolean {
  if (isAdmin) return true;
  if (article.audiences.includes(Department.ALL)) return true;
  return article.audiences.includes(userDepartment);
}
